import { Paperclip, Star } from "lucide-react";

import { cn } from "@/lib/utils";
import type { EmailMessage, ThreadGroup } from "../core/shell-types";
import { receivedLabel, senderHue, senderInitials, senderLabel } from "./format";

function Avatar({ email }: { email: EmailMessage }) {
  return (
    <span
      aria-hidden
      className="flex h-9 w-9 shrink-0 select-none items-center justify-center rounded-full text-[13px] font-semibold text-white"
      style={{ backgroundColor: `oklch(0.55 0.13 ${senderHue(email)})` }}
    >
      {senderInitials(email)}
    </span>
  );
}

function SkeletonRow() {
  return (
    <li aria-hidden className="flex animate-pulse gap-3 px-4 py-3">
      <span className="h-9 w-9 rounded-full bg-muted" />
      <span className="min-w-0 flex-1 space-y-2 py-0.5">
        <span className="block h-3 w-2/5 rounded bg-muted" />
        <span className="block h-3 w-4/5 rounded bg-muted" />
      </span>
    </li>
  );
}

function participantsLabel(thread: ThreadGroup): string {
  if (thread.participantNames.length > 0) {
    return thread.participantNames.join(", ");
  }
  return senderLabel(thread.latestEmail);
}

export function ThreadList({
  compact = false,
  hidden = false,
  threads,
  total,
  loading,
  title,
  selectedThreadId,
  focusedIndex,
  canLoadMore,
  onOpen,
  onLoadMore,
}: {
  // Compact: full width, no divider, 56px+ rows.
  compact?: boolean;
  // Compact only: another pane is up; the list stays mounted (scroll position,
  // loaded pages) but out of the layout.
  hidden?: boolean;
  threads: ThreadGroup[];
  total: number | null;
  loading: boolean;
  title: string;
  selectedThreadId: string | null;
  focusedIndex: number;
  canLoadMore: boolean;
  onOpen: (thread: ThreadGroup) => void;
  onLoadMore: () => void;
}) {
  return (
    <section
      aria-label="Conversations"
      hidden={hidden}
      className={cn(
        "flex-col",
        hidden ? "hidden" : "flex",
        compact ? "min-w-0 flex-1" : "w-[24rem] shrink-0 border-r border-border/70",
      )}
    >
      {compact ? null : (
        // Compact: the app header carries the title.
        <div className="flex items-baseline justify-between px-4 pb-2 pt-4">
          <h2 className="truncate font-display text-lg tracking-tight">{title}</h2>
          <span className="text-xs tabular-nums text-muted-foreground">
            {total !== null && total > 0 ? total : ""}
          </span>
        </div>
      )}
      <ul className={cn("min-h-0 flex-1 overflow-y-auto pb-4", compact && "pt-2")}>
        {loading && threads.length === 0 ? (
          <>
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
          </>
        ) : null}
        {threads.length === 0 && !loading ? (
          <li className="px-4 py-16 text-center text-sm text-muted-foreground">
            Nothing here — enjoy the quiet.
          </li>
        ) : null}
        {threads.map((thread, index) => {
          const latest = thread.latestEmail;
          const unread = thread.hasUnread;
          const selected = thread.threadId === selectedThreadId;
          return (
            <li key={thread.threadId} className="px-2">
              <button
                type="button"
                onClick={() => onOpen(thread)}
                aria-current={selected ? "true" : undefined}
                data-focused={index === focusedIndex || undefined}
                className={cn(
                  "flex w-full gap-3 rounded-lg px-2.5 py-2.5 text-left transition-colors",
                  compact && "min-h-14",
                  selected ? "bg-accent" : "hover:bg-accent/50",
                  // The keyboard cursor is a desktop affordance.
                  !compact &&
                    index === focusedIndex &&
                    !selected &&
                    "ring-1 ring-inset ring-ring/40",
                )}
              >
                <Avatar email={latest} />
                <span className="min-w-0 flex-1">
                  <span className="flex items-baseline justify-between gap-2">
                    <span
                      className={cn(
                        "flex min-w-0 items-baseline gap-1.5 text-sm",
                        unread ? "font-semibold" : "font-medium text-foreground/80",
                      )}
                    >
                      <span className="truncate">{participantsLabel(thread)}</span>
                      {thread.emailCount > 1 ? (
                        <span className="shrink-0 text-xs font-normal tabular-nums text-muted-foreground">
                          {thread.emailCount}
                        </span>
                      ) : null}
                    </span>
                    <span
                      className={cn(
                        "shrink-0 text-xs tabular-nums",
                        unread ? "font-medium text-primary" : "text-muted-foreground",
                      )}
                    >
                      {receivedLabel(latest.receivedAt)}
                    </span>
                  </span>
                  <span className="mt-0.5 flex items-center gap-1.5">
                    {unread ? (
                      <span
                        role="img"
                        aria-label="Unread"
                        className="block h-1.5 w-1.5 shrink-0 rounded-full bg-primary"
                      />
                    ) : null}
                    <span
                      className={cn(
                        "truncate text-sm",
                        unread ? "font-medium" : "text-foreground/70",
                      )}
                    >
                      {latest.subject?.trim() || "(no subject)"}
                    </span>
                    {thread.hasStarred ? (
                      <Star
                        aria-label="Starred"
                        className="h-3.5 w-3.5 shrink-0 fill-amber-400 text-amber-400"
                      />
                    ) : null}
                    {thread.hasAttachment ? (
                      <Paperclip
                        aria-label="Has attachment"
                        className="h-3.5 w-3.5 shrink-0 text-muted-foreground"
                      />
                    ) : null}
                  </span>
                  <span className="mt-0.5 line-clamp-2 block text-xs leading-relaxed text-muted-foreground">
                    {latest.preview ?? ""}
                  </span>
                </span>
              </button>
            </li>
          );
        })}
        {canLoadMore ? (
          <li className="px-4 pt-2">
            <button
              type="button"
              onClick={onLoadMore}
              disabled={loading}
              className="w-full rounded-md border border-input px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:opacity-50"
            >
              {loading ? "Loading…" : "Load more"}
            </button>
          </li>
        ) : null}
      </ul>
    </section>
  );
}
